import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Ellipsis } from "lucide-react";
import PropTypes from "prop-types";
import React, { useState } from "react";
const PAGE_SIZES = [5, 10, 25, 50, 100];
const VISIBLE_PAGES = 5;
const SKELETON_ROWS = 5;

const ActionsDropdown = ({ row, actions: initialActions, isVisible }) => {
  const [open, setOpen] = useState(false);

  const actions = initialActions
    .map((action) => action(row))
    .filter((action) => action !== null);

  if (actions.length === 0)
    return (
      <TableCell>
        <Button
          size="icon"
          variant="borderless"
          className="invisible"
          onClick={(e) => e.preventDefault()}
        >
          <Ellipsis />
        </Button>
      </TableCell>
    );
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <TableCell
          className={`text-center ${isVisible ? "opacity-100" : "opacity-0"}`}
          onClick={(e) => e.preventDefault()}
        >
          <Button
            size="icon"
            variant="borderless"
            className="text-secondary"
            onClick={(e) => e.preventDefault()}
          >
            <Ellipsis />
          </Button>
        </TableCell>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-background">
        <div className="flex flex-col gap-2 justify-start align-middle items-start">
          {actions.map((action, index) => {
            return action;
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const TableSkeleton = ({ columns, hasActions }) => (
  <>
    {[...Array(SKELETON_ROWS)].map((_, rowIndex) => (
      <TableRow key={`skeleton-row-${rowIndex}`}>
        {columns.map((column, colIndex) => (
          <TableCell key={`skeleton-cell-${rowIndex}-${colIndex}`}>
            <Skeleton className="h-4 w-[80%]" />
          </TableCell>
        ))}
        {hasActions && (
          <TableCell className="text-center">
            <Skeleton className="h-8 w-8 rounded-full mx-auto" />
          </TableCell>
        )}
      </TableRow>
    ))}
  </>
);

const CustomTableRow = ({ columns, row, actions }) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <TableRow
      key={row.id}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {columns.map((column) => (
        <TableCell key={column.title}>
          {column.render ? column.render(row) : row[column.accessor]}
        </TableCell>
      ))}
      {actions.length > 0 && (
        <ActionsDropdown row={row} actions={actions} isVisible={isHovered} />
      )}
    </TableRow>
  );
};
const CustomTable = ({
  columns = [],
  data = [],
  actions = [],
  paginated = false,
  pagination = { page: 0, size: 50, totalElements: 0 },
  onPageChange,
  onPageSizeChange,
  isLoading,
}) => {
  const totalPages = Math.ceil(pagination.totalElements / pagination.size);
  const currentPage = pagination.page + 1; // Convert 0-based to 1-based for display

  const getVisiblePages = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - Math.floor(VISIBLE_PAGES / 2));
    const endPage = Math.min(totalPages, startPage + VISIBLE_PAGES - 1);

    // Always show first page
    if (startPage > 1) {
      pages.push(1);
      if (startPage > 2) pages.push("ellipsis");
    }

    // Add visible pages
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    // Always show last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) pages.push("ellipsis");
      pages.push(totalPages);
    }

    return pages;
  };

  const handlePageChange = (page) => {
    if (onPageChange) {
      onPageChange(page - 1); // Convert 1-based back to 0-based
    }
  };

  const handlePageSizeChange = (newSize) => {
    if (onPageSizeChange) {
      onPageSizeChange(Number(newSize));
    }
  };

  const from =
    pagination.totalElements === 0 ? 0 : pagination.page * pagination.size + 1;
  const to = Math.min(
    (pagination.page + 1) * pagination.size,
    pagination.totalElements
  );

  return (
    <div className="flex flex-col">
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHead key={column.title}>
                {column.headerRender ? (
                  column.headerRender()
                ) : (
                  <span>{column.title}</span>
                )}
              </TableHead>
            ))}
            {actions.length > 0 && (
              <TableHead className="text-center"></TableHead>
            )}
          </TableRow>
        </TableHeader>
        {isLoading ? (
          <TableSkeleton columns={columns} hasActions={actions.length > 0} />
        ) : (
          <TableBody>
            {data.map((d, index) => (
              <CustomTableRow
                key={d.id}
                columns={columns}
                row={d}
                actions={actions}
              />
            ))}
          </TableBody>
        )}
      </Table>

      {paginated && pagination.totalElements > 0 && (
        <Pagination>
          <PaginationContent>
            {/* <PaginationSizeSelect
              value={pagination.size}
              onValueChange={handlePageSizeChange}
              sizes={PAGE_SIZES}
            />

            <PaginationInfo
              from={from}
              to={to}
              total={pagination.totalElements}
            /> */}

            {/* <div className="flex items-center gap-2"> */}
            {
              //only if the current page is not the first page
              <PaginationPrevious
                onClick={() => handlePageChange(currentPage - 1)}
                className={
                  currentPage === 1
                    ? "pointer-events-none opacity-50"
                    : "cursor-pointer"
                }
              />
            }
            {getVisiblePages().map((page, index) => (
              <PaginationItem key={index}>
                {page === "ellipsis" ? (
                  <PaginationEllipsis />
                ) : (
                  <PaginationLink
                    onClick={() => handlePageChange(page)}
                    isActive={page === currentPage}
                    className="cursor-pointer"
                  >
                    {page}
                  </PaginationLink>
                )}
              </PaginationItem>
            ))}

            {
              //only if the current page is not the last page
              currentPage !== totalPages && (
                <PaginationNext
                  onClick={() => {
                    handlePageChange(currentPage + 1);
                  }}
                  className={
                    currentPage === totalPages
                      ? "pointer-events-none opacity-50"
                      : "cursor-pointer"
                  }
                />
              )
            }
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
};

// PropTypes definition
CustomTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      accessor: PropTypes.string,
      headerRender: PropTypes.func,
      render: PropTypes.func,
    })
  ),
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ),
  actions: PropTypes.arrayOf(PropTypes.func),
  paginated: PropTypes.bool,
  pagination: PropTypes.shape({
    page: PropTypes.number,
    size: PropTypes.number,
    totalElements: PropTypes.number,
  }),
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  className: PropTypes.string,
};

CustomTable.defaultProps = {
  columns: [],
  data: [],
  actions: [],
  paginated: false,
  pagination: {
    page: 0,
    size: 50,
    totalElements: 0,
  },
};

export default CustomTable;
